import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridRowParams,
  type GridRowsProp,
} from '@mui/x-data-grid-premium';
import { Trash2Icon } from 'lucide-react';

import type { GetOrganisationOverviewQuery } from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TeamAvatar } from '@modules/_shared/components/TeamAvatar';
import { TeamsSelect } from '@modules/_shared/components/teams-select/TeamsSelect';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { OrganisationScoreCards } from '@modules/organisations/components/OrganisationScoreCards';
import {
  ADD_TEAM_TO_ORGANISATION,
  GET_ORGANISATION_OVERVIEW,
  REMOVE_TEAM_FROM_ORGANISATION,
} from '@modules/organisations/organisationsQueries';
import { useLocale } from '@shared/hooks/locale';

type Team = NonNullable<
  GetOrganisationOverviewQuery['organisations_by_pk']
>['teams'][number];

export const OrganisationOverview: React.FC<{ organisationId: string }> = ({
  organisationId,
}) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);
  const [addTeamToOrganisation, { loading: addingTeam }] = useMutation(
    ADD_TEAM_TO_ORGANISATION,
    { onError: setErrorLike, refetchQueries: [GET_ORGANISATION_OVERVIEW] },
  );
  const [removeTeamFromOrganisation] = useMutation(
    REMOVE_TEAM_FROM_ORGANISATION,
    {
      onError: error => {
        setErrorLike(error);
        setDeletingRowId(null);
      },
      onCompleted: () => {
        setDeletingRowId(null);
      },
      refetchQueries: [GET_ORGANISATION_OVERVIEW],
    },
  );
  const { data, error, loading } = useQuery(GET_ORGANISATION_OVERVIEW, {
    variables: { id: organisationId },
    // Reopening the drawer must reflect teams added/removed elsewhere, so read
    // from cache for instant paint but always revalidate against the network.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const rows: GridRowsProp<Team> = data?.organisations_by_pk?.teams ?? [];

  const columns: GridColDef<Team>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Name'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        renderCell: ({ row }) => (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ position: 'relative' }}>
              <TeamAvatar logoUrl={row?.logo?.url} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div>{row?.name}</div>
            </div>
          </div>
        ),
      },
      {
        field: 'postcode,locality,route,street_number',
        headerName: t('Address'),
        width: 200,
        display: 'flex',
        renderCell: ({ row }) => (
          <Stack>
            {formatAddress(row, null).map((line, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={theme => ({
                  ...theme.text.ellipsis,
                })}
              >
                {line}
              </Typography>
            ))}
          </Stack>
        ),
      },
      {
        field: 'teams_users.lots_aggregate.aggregate.count',
        headerName: t('Listings'),
        sortable: false,
        renderCell: ({ row }) =>
          row.teams_users.reduce(
            (acc, tu) => acc + (tu.user.lots_aggregate?.aggregate?.count ?? 0),
            0,
          ),
      },
      {
        field: 'teams_users.broker_leads_aggregate.aggregate.count',
        headerName: t('Leads'),
        sortable: false,
        renderCell: ({ row }) =>
          row.teams_users.reduce(
            (acc, tu) =>
              acc + (tu.user.broker_leads_aggregate?.aggregate?.count ?? 0),
            0,
          ),
      },
      {
        field: 'teams_users_aggregate.aggregate.count',
        headerName: t('Members'),
        renderCell: ({ row }) => row.teams_users_aggregate?.aggregate?.count,
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams<Team>) => [
          <GridActionsCellItem
            icon={<Trash2Icon size={18} />}
            label="Remove from organisation"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setDeletingRowId(params.row.id);
              removeTeamFromOrganisation({
                variables: {
                  team_id: params.row.id,
                },
              });
            }}
            disabled={deletingRowId === params.row.id}
            color="inherit"
          />,
        ],
      },
    ],
    [t, removeTeamFromOrganisation, deletingRowId],
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <Stack
      sx={{
        p: 2,
      }}
    >
      <OrganisationScoreCards
        organisation={data?.organisations_by_pk}
        loading={loading}
      />
      <Box sx={{ mb: 1, maxWidth: 300 }}>
        <TeamsSelect
          onChange={teamId =>
            teamId != null &&
            addTeamToOrganisation({
              variables: {
                team_id: teamId,
                organisation_id: organisationId,
              },
            })
          }
          loading={addingTeam}
          filters={{ organisation_id: { _is_null: true } }}
          excludeTeamsIds={
            data?.organisations_by_pk?.teams.map(team => team.id) ?? []
          }
          blurOnSelect
          clearOnBlur
          InputProps={{
            placeholder: t('Add team to organisation'),
          }}
        />
      </Box>
      <DataGridPremium
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        rows={rows}
        columns={columns}
        loading={loading}
        rowReordering={false}
        getRowId={row => row.id}
        onRowClick={({ row }) => {
          window.open(`/teams/${row.id}`, '_blank');
        }}
        getRowClassName={params =>
          deletingRowId === params.row.id ? 'row-deleting' : ''
        }
        rowHeight={64}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 4,
          '& .MuiDataGrid-main': {
            borderRadius: 4,
          },
          '& .MuiDataGrid-row.row-deleting': {
            opacity: 0.5,
            pointerEvents: 'none',
            '& .MuiDataGrid-cell': {
              color: 'text.disabled',
            },
          },
          // pointer cursor on ALL rows
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Stack>
  );
};
